import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BottleneckEntry } from '../hooks/useOverviewData';
import { formatNumber } from '../utils/format';
import { downloadCsv, todayYmd } from '../utils/csv';
import LeaderboardCard, { LeaderboardDelta, LeaderboardItem } from './LeaderboardCard';

interface Props {
    entries: BottleneckEntry[] | null;
    // Previous-period rework counts. Optional so legacy callers (no period
    // comparison) skip the delta column entirely. Severity is inverted vs
    // TopClientsCard — more rework = bad, so an up-arrow is negative.
    entriesPrevious?: BottleneckEntry[] | null;
    loading: boolean;
    // Page passes 3 on mobile to keep this card short; "Show all" still reveals up to 10.
    defaultRows?: number;
}

const formatPct = (pct: number, locale: string): string => {
    return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 0,
    }).format(pct) + '%';
};

const BottlenecksCard: React.FC<Props> = ({ entries, entriesPrevious, loading, defaultRows }) => {
    const intl = useIntl();
    const locale = intl.locale;

    const previousById = useMemo(() => {
        const map = new Map<number, BottleneckEntry>();
        (entriesPrevious ?? []).forEach((e) => map.set(e.responsibility_id, e));
        return map;
    }, [entriesPrevious]);

    const items: LeaderboardItem[] | null = useMemo(() => {
        if (!entries) return null;
        return entries.map((e) => {
            const prev = previousById.get(e.responsibility_id);
            let delta: LeaderboardDelta | undefined;
            if (entriesPrevious != null) {
                if (!prev || prev.total_reworks <= 0) {
                    // No prior rework data — surface as "NEW" (negative for bottlenecks,
                    // since the responsibility just started accumulating rework).
                    delta = {
                        severity: 'negative',
                        label: (
                            <FormattedMessage
                                id="analyticsV2.leaderboard.deltaNew"
                                defaultMessage="NEW"
                            />
                        ),
                    };
                } else {
                    const pct = ((e.total_reworks - prev.total_reworks) / prev.total_reworks) * 100;
                    const rounded = Math.round(pct * 10) / 10;
                    if (Math.abs(rounded) < 0.1) {
                        delta = { severity: 'neutral', label: '—' };
                    } else if (rounded > 0) {
                        // Rework increased = bad
                        delta = { severity: 'negative', label: `↑ ${formatPct(rounded, locale)}` };
                    } else {
                        // Rework decreased = good
                        delta = { severity: 'positive', label: `↓ ${formatPct(Math.abs(rounded), locale)}` };
                    }
                }
            }
            return {
                id: e.responsibility_id,
                primary: e.name,
                value: e.total_reworks,
                valueDisplay: formatNumber(e.total_reworks, locale),
                meta: (
                    <FormattedMessage
                        id="analyticsV2.bottlenecks.affected"
                        defaultMessage="{count, plural, one {# product affected} other {# products affected}}"
                        values={{ count: e.affected_products }}
                    />
                ),
                delta,
            };
        });
    }, [entries, entriesPrevious, previousById, locale]);

    const handleExport = () => {
        if (!entries || entries.length === 0) return;
        downloadCsv<BottleneckEntry & { rank: number }>(
            `top-bottlenecks-${todayYmd()}.csv`,
            [
                { header: 'Rank', accessor: (r) => r.rank },
                { header: 'Stage', accessor: (r) => r.name },
                { header: 'Rework count', accessor: (r) => r.total_reworks },
                { header: 'Products affected', accessor: (r) => r.affected_products },
            ],
            entries.map((e, i) => ({ ...e, rank: i + 1 })),
        );
    };

    return (
        <LeaderboardCard
            title={<FormattedMessage id="analyticsV2.bottlenecks.title" defaultMessage="Top bottlenecks" />}
            subtitle={
                <FormattedMessage
                    id="analyticsV2.bottlenecks.subtitle"
                    defaultMessage="Stages with the most rework"
                />
            }
            items={items}
            loading={loading}
            accent="warning"
            emptyMessage={
                <FormattedMessage
                    id="analyticsV2.bottlenecks.empty"
                    defaultMessage="No rework events in this period — well done."
                />
            }
            onExport={handleExport}
            defaultRows={defaultRows}
        />
    );
};

export default BottlenecksCard;
